<script lang="ts">
    import {
        DeleteFeedPostMutationDocument,
        type NewsFeedPostFragment,
    } from '$gql/graphql.js';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import ButtonMenu from '$components/ButtonMenu/ButtonMenu.svelte';
    import MenuLink from '$components/ButtonMenu/MenuLink.svelte';
    import { cx } from '$lib/utils/cx.js';
    import { showConfirmModal } from '$components/ConfirmModal/Utils.js';
    import * as t from '$trans';

    const container = getContainer();
    const { apollo, lang } = getContainer();

    export let post: NewsFeedPostFragment;
    export let className: string = '';

    export let onReport: () => void;
    export let onEdit: () => void;

    let actions = {
        edit: {
            type: 'async' as const,
            action: async () => {
                onEdit();
                return {
                    type: 'success' as const,
                };
            },
        },
        report: {
            type: 'async' as const,
            action: async () => {
                onReport();
                return {
                    type: 'success' as const,
                };
            },
        },
        delete: {
            type: 'async' as const,
            action: async () => {
                if (
                    !(await showConfirmModal({
                        message: t.feed_post.confirm_modal_delete_post_message({}, $lang),
                        container,
                    }))
                ) {
                    return {
                        type: 'success' as const,
                    };
                }

                const result = await apollo().mutate({
                    mutation: DeleteFeedPostMutationDocument,
                    variables: {
                        id: post.id,
                    },
                    //refetchQueries: [NewsFeedDocument],
                    awaitRefetchQueries: true,
                    update(cache, result) {
                        if (result.data?.feed.deleteFeedPost) {
                            const normalizedId = cache.identify({
                                id: post.id,
                                __typename: post.__typename,
                            });
                            cache.evict({ id: normalizedId });
                            cache.gc();
                        }
                    },
                });

                const success = result.data?.feed.deleteFeedPost ?? false;

                if (success) {
                    return {
                        type: 'success' as const,
                        toast: {
                            message: 'Post supprimé avec succès',
                        },
                    };
                }

                return {
                    type: 'error' as const,
                    message: result.errors?.map((e) => e.message)?.join(',') || '',
                };
            },
        },
    };
</script>

<ButtonMenu
    class={cx(
        'btn btn-default border-none rounded-full text-14 text-gray-800 w-[32px] h-[32px] flex-none shadow-xl_ bg-transparent',
        className,
    )}
    {actions}
>
    <i slot="button" class="fa fa-ellipsis-v" aria-hidden={true}></i>
    <svelte:fragment slot="menu">
        {#if post.meCanEdit}
            <MenuLink
                icon="fa fa-pencil"
                suffixIcon={post.meCanEditAsAdmin ? 'fa fa-shield' : null}
                key="edit"
            >
                {t.feed_post.menu_edit_the_post({}, $lang)}
            </MenuLink>
        {/if}
        {#if post.meCanDelete}
            <MenuLink
                icon="fa fa-trash"
                suffixIcon={post.meCanDeleteAsAdmin ? 'fa fa-shield' : null}
                key="delete"
            >
                {t.feed_post.menu_delete_the_post({}, $lang)}
            </MenuLink>
        {/if}
        {#if !post.meCanEdit || post.meCanDeleteAsAdmin}
            <MenuLink icon="fa fa-exclamation-triangle" key="report">
                {t.feed_post.menu_signal_the_post({}, $lang)}
            </MenuLink>
        {/if}
    </svelte:fragment>
</ButtonMenu>
